import { Box, Typography, FormControlLabel, Checkbox, useTheme } from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { Asset, ApiSystem } from "../../state/types";
import { useAsset } from "../../context/AssetContext";
import { useSession } from "../../context/SessionContext";

const AssetSidebar: React.FC = () => {
  const theme = useTheme();
  const { systems } = useSession()

  const {
    asset,
    visibleTypes,
    setVisibleTypes,
    imageCategories
  } = useAsset();

  console.log(asset)

  if (!asset) return <></>

  const handleCheckboxChange = (key: string) => {
    setVisibleTypes((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const getSyndicationStep = (asset: Asset) => {
    if (!asset || !asset.syndicationSteps || !asset.syndicationCurrentStep) return ''

    if (asset.syndicationCurrentStep === "COMPLETE") return `${asset?.syndicationSteps[0]} COMPLETE`

    return asset.syndicationCurrentStep
  }

  const getCommonNameForSystem = (systemCode: string) => {
    return systems?.find((sys: ApiSystem) => sys.systemCode === systemCode)?.commonName
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        position: { xs: "relative", md: "sticky" },
        top: { md: "5px" },
        height: "100%",
        borderRight: { md: `5px solid ${theme.palette.divider}` },
        borderBottom: { xs: `5px solid ${theme.palette.divider}`, md: "none" },
        overflowY: "auto",
        zIndex: 2,
        p: 2
      }}
    >
      <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: "bold", mb: 2 }}>
        Asset Info
      </Typography>

      <Typography variant="body2" sx={{ mb: 1 }}>
        <strong>Ran at:</strong> {new Date(+asset.created_at * 1000).toLocaleString()}
      </Typography>
      {Array.isArray((asset as any).assetStorage_merges) && (asset as any).assetStorage_merges.length > 0 && (
        <Box sx={{ mt: 1, mb: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 0.5 }}>
            Merge History
          </Typography>

          <Box sx={{ pl: 1 }}>
            {(asset as any).assetStorage_merges
              .slice()
              .sort((a: number, b: number) => b - a) // newest first
              .map((ts: number) => (
                <Typography key={ts} variant="body2" sx={{ mb: 0.25 }}>
                  • {new Date(ts * 1000).toLocaleString()}
                </Typography>
              ))}
          </Box>
        </Box>
      )}
      <Typography variant="body2" sx={{ mb: 1 }}>
        <strong>Asset ID:</strong> {asset.assetId}
      </Typography>
      <Typography variant="body2" sx={{ mb: 1 }}>
        <strong>System Code:</strong> {asset.systemCode}
      </Typography>
      <Typography variant="body2" sx={{ mb: 1 }}>
        <strong>Client Name:</strong> {getCommonNameForSystem(asset.systemCode || "")}
      </Typography>
      <Typography variant="body2" sx={{ mb: 1 }}>
        <strong>Config Name:</strong> {asset.config}
      </Typography>
      <Typography variant="body2" sx={{ mb: 1 }}>
        <strong>Processing Status:</strong> {asset.processingCurrentStep?.toUpperCase()}
      </Typography>

      {asset.processingError && (
        <Box
          sx={{
            backgroundColor: theme.palette.error.main,
            color: "white",
            borderRadius: 1,
            py: 1,
            px: 2,
            mb: 1,
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <WarningAmberIcon sx={{ fontSize: "1.2rem" }} />
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {asset.processingError}
          </Typography>
        </Box>
      )}

      <Typography variant="body2" sx={{ mb: 2 }}>
        <strong>Syndication Status:</strong> {getSyndicationStep(asset)}
      </Typography>

      {asset.syndicationError && (
        <Box
          sx={{
            backgroundColor: theme.palette.error.main,
            color: "white",
            borderRadius: 1,
            py: 1,
            px: 2,
            mb: 1,
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <WarningAmberIcon sx={{ fontSize: "1.2rem" }} />
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {asset.syndicationError}
          </Typography>
        </Box>
      )}

      <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 1 }}>
        Image Categories
      </Typography>

      {imageCategories.map(({ key, label, images }) => {
        const defaultVisible = images.length > 0;
        const isChecked = (visibleTypes[key] ?? defaultVisible);
        return (
          <FormControlLabel
            key={key}
            control={
              <Checkbox
                checked={isChecked}
                onChange={() => handleCheckboxChange(key)}
                disabled={!images.length}
                sx={{
                  "&.Mui-checked": {
                    color: (theme) => theme.palette.status.accent,
                  },
                }}
              />
            }
            label={`${label} (${images.length})`}
            sx={{
              opacity: images.length ? 1 : 0.5,
            }}
          />
        )
      })}
    </Box>
  );
};

export default AssetSidebar;
